import React from 'react';

interface Step {
  number: number;
  title: string;
  dateRange: string;
  description?: string;
  color: string;
  iconType:
    | 'preparation'
    | 'submission'
    | 'qualification'
    | 'judging'
    | 'winner';
  side: 'left' | 'right';
}

const steps: Step[] = [
  {
    number: 1,
    title: 'Preparation Phase',
    dateRange: '9 - 20 May 2026',
    color: '#0284C7', // Sky Blue
    iconType: 'preparation',
    side: 'right',
  },
  {
    number: 2,
    title: 'PPT Project Submission (in PDF)',
    dateRange: '20 May - 30 June 2026',
    color: '#4F46E5', // Indigo
    iconType: 'submission',
    side: 'left',
  },
  {
    number: 3,
    title: 'Qualification Stage',
    description: 'Top 96 + Online Presentation',
    dateRange: '10 - 21 August 2026',
    color: '#7C3AED', // Violet
    iconType: 'qualification',
    side: 'right',
  },
  {
    number: 4,
    title: 'Executive Judging',
    description: 'Offline Session',
    dateRange: '18 September 2026',
    color: '#ED1C24', // Red
    iconType: 'judging',
    side: 'left',
  },
  {
    number: 5,
    title: 'Winner Announcement',
    dateRange: '24 October 2026',
    color: '#D97706', // Amber (high contrast yellow/orange)
    iconType: 'winner',
    side: 'right',
  },
];

const renderIcon = (
  type: 'preparation' | 'submission' | 'qualification' | 'judging' | 'winner',
  color: string
) => {
  const strokeColor = color;
  const strokeWidth = 1.75;
  const fillBg = `${color}12`; // 7% opacity fill

  switch (type) {
    case 'preparation':
      return (
        <svg
          className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <rect
            x="5"
            y="4"
            width="14"
            height="16"
            rx="2"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill={fillBg}
          />
          <path d="M9 3H15V6H9V3Z" fill={strokeColor} />
          <path
            d="M9 11L11 13L15 9"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <line
            x1="8"
            y1="16"
            x2="16"
            y2="16"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'submission':
      return (
        <svg
          className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill={fillBg}
          />
          <path
            d="M14 2V8H20"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinejoin="round"
          />
          <rect
            x="7"
            y="12"
            width="10"
            height="6"
            rx="1.5"
            fill={strokeColor}
          />
          <text
            x="12"
            y="16.5"
            fill="#FFFFFF"
            fontSize="5"
            fontWeight="900"
            textAnchor="middle"
            fontFamily="ui-sans-serif, system-ui, sans-serif"
            letterSpacing="0.2"
          >
            PDF
          </text>
        </svg>
      );
    case 'qualification':
      return (
        <svg
          className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="9"
            cy="8"
            r="3.5"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill={fillBg}
          />
          <path
            d="M3 17.5C3 15 6.5 14.5 9 14.5C10.5 14.5 12.3 14.7 13.3 15.5"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <circle
            cx="16"
            cy="14.5"
            r="3.5"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill="#FFFFFF"
          />
          <path
            d="M18.5 17L21 19.5"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'judging':
      return (
        <svg
          className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle
            cx="12"
            cy="7"
            r="3"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill={fillBg}
          />
          <path
            d="M6 18.5C6 15.5 9 14 12 14C15 14 18 15.5 18 18.5V20.5H6V18.5Z"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill={fillBg}
          />
          <path
            d="M4 9C4.8 9 5.5 8.3 5.5 7.5C5.5 6.7 4.8 6 4 6"
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <path
            d="M2 18.5C2 16.5 3.5 16 5 16"
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <path
            d="M20 9C19.2 9 18.5 8.3 18.5 7.5C18.5 6.7 19.2 6 20 6"
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
          <path
            d="M22 18.5C22 16.5 20.5 16 19 16"
            stroke={strokeColor}
            strokeWidth={1.5}
            strokeLinecap="round"
          />
        </svg>
      );
    case 'winner':
      return (
        <svg
          className="w-10 h-10 transition-transform duration-300 group-hover:scale-110"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M6 9H4C2.89543 9 2 8.10457 2 7C2 5.89543 2.89543 5 4 5H6V9ZM6 9V12C6 14.7614 8.23858 17 11 17H13C15.7614 17 18 14.7614 18 12V9ZM18 9H20C21.1046 9 22 8.10457 22 7C22 5.89543 21.1046 5 20 5H18V9Z"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            fill={fillBg}
            strokeLinejoin="round"
          />
          <path
            d="M12 17V20.5"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
          <path
            d="M8 20.5H16"
            stroke={strokeColor}
            strokeWidth={strokeWidth}
            strokeLinecap="round"
          />
        </svg>
      );
    default:
      return null;
  }
};

const TimelinePrizesSection: React.FC = () => (
  <section id="timeline" className="bg-white py-20 relative overflow-hidden">
    {/* Elegant subtle backdrops */}
    <div className="absolute top-1/4 left-0 w-72 h-72 bg-sky-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float" />
    <div
      className="absolute bottom-1/4 right-0 w-80 h-80 bg-rose-50 rounded-full mix-blend-multiply filter blur-3xl opacity-30 animate-float"
      style={{ animationDelay: '1.5s' }}
    />

    <div className="max-w-5xl mx-auto px-6 relative z-10">
      <div className="text-center mb-20">
        <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-50 text-smdd-red rounded-full text-xs font-semibold uppercase tracking-wider mb-6">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path
              fillRule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z"
              clipRule="evenodd"
            />
          </svg>
          Awards 2026 Timeline
        </div>
        <h2 className="text-4xl md:text-5xl font-extrabold text-gray-900 font-bai tracking-tight mb-4">
          How It Works
        </h2>
        <p className="text-gray-600 max-w-2xl mx-auto text-lg leading-relaxed">
          Follow the key stages of Sinar Mas Digital Day Awards 2026, from
          initial preparation to the grand winner announcement.
        </p>
      </div>

      {/* Timeline wrapper */}
      <div className="relative max-w-5xl mx-auto">
        {/* Desktop Vertical Line */}
        <div className="hidden md:block absolute left-1/2 top-10 bottom-10 w-0.5 bg-gray-200 transform -translate-x-1/2" />

        {/* Mobile Vertical Line */}
        <div className="md:hidden absolute left-6 top-6 bottom-6 w-0.5 bg-gray-200" />

        {/* Steps */}
        <div className="space-y-4 md:space-y-12">
          {steps.map(step => {
            const isLeft = step.side === 'left';
            return (
              <div
                key={step.number}
                className="relative flex flex-col md:flex-row items-start md:items-center min-h-[140px] group"
              >
                {/* Center Badge for Desktop */}
                <div
                  className="hidden md:flex absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-12 h-12 rounded-full items-center justify-center border-4 border-white text-white font-bold text-lg shadow-md transition-transform duration-300 group-hover:scale-110"
                  style={{ backgroundColor: step.color }}
                >
                  {String(step.number).padStart(2, '0')}
                </div>

                {/* Left Side (Desktop) */}
                <div
                  className={`hidden md:flex w-1/2 px-12 ${isLeft ? 'justify-end text-right' : 'justify-end'}`}
                >
                  {isLeft ? (
                    /* Content for left-side step */
                    <div className="max-w-md transition-all duration-300 group-hover:translate-x-[-4px]">
                      <h3 className="text-2xl font-bold text-gray-900 font-bai mb-1 tracking-tight">
                        {step.title}
                      </h3>
                      {step.description && (
                        <p className="text-sm text-gray-500 mb-2 font-medium">
                          {step.description}
                        </p>
                      )}
                      <p
                        className="text-lg font-bold"
                        style={{ color: step.color }}
                      >
                        {step.dateRange}
                      </p>
                    </div>
                  ) : (
                    /* Icon for right-side step (icon goes on left) */
                    <div
                      className="w-24 h-24 rounded-full flex items-center justify-center border-4 bg-white shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md"
                      style={{ borderColor: step.color }}
                    >
                      {renderIcon(step.iconType, step.color)}
                    </div>
                  )}
                </div>

                {/* Right Side (Desktop) */}
                <div
                  className={`hidden md:flex w-1/2 px-12 ${isLeft ? 'justify-start' : 'justify-start text-left'}`}
                >
                  {isLeft ? (
                    /* Icon for left-side step (icon goes on right) */
                    <div
                      className="w-24 h-24 rounded-full flex items-center justify-center border-4 bg-white shadow-sm transition-all duration-300 group-hover:scale-105 group-hover:shadow-md"
                      style={{ borderColor: step.color }}
                    >
                      {renderIcon(step.iconType, step.color)}
                    </div>
                  ) : (
                    /* Content for right-side step */
                    <div className="max-w-md transition-all duration-300 group-hover:translate-x-[4px]">
                      <h3 className="text-2xl font-bold text-gray-900 font-bai mb-1 tracking-tight">
                        {step.title}
                      </h3>
                      {step.description && (
                        <p className="text-sm text-gray-500 mb-2 font-medium">
                          {step.description}
                        </p>
                      )}
                      <p
                        className="text-lg font-bold"
                        style={{ color: step.color }}
                      >
                        {step.dateRange}
                      </p>
                    </div>
                  )}
                </div>

                {/* Mobile Layout */}
                <div className="flex md:hidden w-full pl-14 pr-4 py-6 relative flex-row items-center gap-5">
                  {/* Mobile Badge */}
                  <div
                    className="absolute left-6 top-1/2 -translate-x-1/2 -translate-y-1/2 z-20 w-9 h-9 rounded-full flex items-center justify-center border-2 border-white text-white font-bold text-sm shadow-sm"
                    style={{ backgroundColor: step.color }}
                  >
                    {String(step.number).padStart(2, '0')}
                  </div>

                  {/* Mobile Icon */}
                  <div
                    className="w-16 h-16 rounded-full flex-shrink-0 flex items-center justify-center border-2 bg-white shadow-sm"
                    style={{ borderColor: step.color }}
                  >
                    {renderIcon(step.iconType, step.color)}
                  </div>

                  {/* Mobile Content */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-base font-bold text-gray-900 font-bai mb-0.5 leading-tight tracking-tight">
                      {step.title}
                    </h3>
                    {step.description && (
                      <p className="text-xs text-gray-500 mb-1 font-medium">
                        {step.description}
                      </p>
                    )}
                    <p
                      className="text-sm font-bold"
                      style={{ color: step.color }}
                    >
                      {step.dateRange}
                    </p>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  </section>
);

export default TimelinePrizesSection;
